import React, { useState } from 'react';
import {
  Box,
  TextField,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  Typography,
  Tooltip,
  Slider,
  Popover,
  Button,
  InputAdornment,
  Tabs,
  Tab,
  Stack,
  alpha,
  useTheme,
  ThemeProvider,
  createTheme
} from '@mui/material';
import {
  Close as CloseIcon,
  Link as LinkIcon,
  LinkOff as LinkOffIcon,
  FormatAlignLeft as AlignLeftIcon,
  FormatAlignCenter as AlignCenterIcon,
  FormatAlignRight as AlignRightIcon,
  FormatTextdirectionLToR as FloatLeftIcon,
  FormatTextdirectionRToL as FloatRightIcon,
  Block as NoFloatIcon,
  Photo as PhotoIcon,
  BorderOuter as BorderIcon,
  FlareOutlined as ShadowIcon,
  SpaceBar as MarginIcon,
  TextFields as AltTextIcon,
  AspectRatio as AspectRatioIcon,
  PhotoSizeSelectSmall as SmallIcon,
  PhotoSizeSelectLarge as MediumIcon,
  PhotoSizeSelectActual as LargeIcon,
  Fullscreen as FullIcon
} from '@mui/icons-material';

const ImageEditToolbarContent = ({ 
  image, 
  onUpdate, 
  onClose,
  aspectRatio,
  position = 'right'
}) => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [popoverType, setPopoverType] = useState(null);
  
  // State
  const [dimensions, setDimensions] = useState({
    width: parseInt(image.width) || image.naturalWidth,
    height: parseInt(image.height) || image.naturalHeight
  });
  const [maintainRatio, setMaintainRatio] = useState(true);
  // Get initial values
  const initialAlign = image.getAttribute('data-align') || 'left';
  const initialFloat = image.getAttribute('data-float') || null;
  
  const [settings, setSettings] = useState({
    align: initialAlign,
    float: initialFloat,
    alt: image.getAttribute('alt') || '',
    link: image.getAttribute('data-link') || '',
    margins: {
      top: parseInt(image.getAttribute('data-margin-top')) || 0,
      right: parseInt(image.getAttribute('data-margin-right')) || 0,
      bottom: parseInt(image.getAttribute('data-margin-bottom')) || 0,
      left: parseInt(image.getAttribute('data-margin-left')) || 0
    },
    border: {
      width: parseInt(image.getAttribute('data-border-width')) || 0,
      color: image.getAttribute('data-border-color') || '#cccccc',
      radius: parseInt(image.getAttribute('data-border-radius')) || 0
    },
    shadow: image.getAttribute('data-shadow') || 'none'
  });

  // Handle dimension change
  const handleDimensionChange = (dimension, value) => {
    const newValue = parseInt(value) || 0;
    const newDimensions = { ...dimensions };
    
    if (dimension === 'width') {
      newDimensions.width = newValue;
      if (maintainRatio && newValue > 0) {
        newDimensions.height = Math.round(newValue / aspectRatio);
      }
    } else {
      newDimensions.height = newValue;
      if (maintainRatio && newValue > 0) {
        newDimensions.width = Math.round(newValue * aspectRatio);
      }
    }
    
    setDimensions(newDimensions);
    onUpdate({ width: newDimensions.width, height: newDimensions.height });
  };

  // Handle popover
  const handleOpenPopover = (event, type) => {
    setAnchorEl(event.currentTarget);
    setPopoverType(type);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
    setPopoverType(null);
  };

  // Apply preset size
  const applyPresetSize = (preset) => {
    const sizes = {
      small: { width: 200 },
      medium: { width: 400 },
      large: { width: 600 },
      full: { width: '100%' }
    };
    
    if (preset === 'full') {
      onUpdate({ width: false, height: false, 'data-align': 'center' });
      setDimensions({ width: 0, height: 0 });
    } else {
      handleDimensionChange('width', sizes[preset].width);
    }
  };

  const open = Boolean(anchorEl);
  
  // Update settings when image attributes change
  React.useEffect(() => {
    const newAlign = image.getAttribute('data-align') || 'left';
    const newFloat = image.getAttribute('data-float') || null;
    
    setSettings(prev => ({
      ...prev,
      align: newAlign,
      float: newFloat
    }));
  }, [image]);

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        border: 1,
        borderColor: 'divider',
        borderRadius: 1.5,
        boxShadow: theme.shadows[4],
        overflow: 'visible',
        minWidth: 280,
        maxWidth: 320,
        fontSize: '0.75rem',
        position: 'relative',
        // Add arrow/caret based on position
        '&::before': position !== 'top' && position !== 'bottom' ? {
          content: '""',
          position: 'absolute',
          top: '20px',
          width: 0,
          height: 0,
          borderStyle: 'solid',
          ...(position === 'left' ? {
            right: '-8px',
            borderWidth: '8px 0 8px 8px',
            borderColor: `transparent transparent transparent ${theme.palette.divider}`,
          } : {
            left: '-8px',
            borderWidth: '8px 8px 8px 0',
            borderColor: `transparent ${theme.palette.divider} transparent transparent`,
          })
        } : {},
        '&::after': position !== 'top' && position !== 'bottom' ? {
          content: '""',
          position: 'absolute',
          top: '20px',
          width: 0,
          height: 0,
          borderStyle: 'solid',
          ...(position === 'left' ? {
            right: '-7px',
            borderWidth: '7px 0 7px 7px',
            borderColor: `transparent transparent transparent ${theme.palette.background.paper}`,
          } : {
            left: '-7px',
            borderWidth: '7px 7px 7px 0',
            borderColor: `transparent ${theme.palette.background.paper} transparent transparent`,
          })
        } : {}
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1.5,
          py: 0.75,
          bgcolor: alpha(theme.palette.primary.main, 0.06),
          borderBottom: 1,
          borderColor: 'divider'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <PhotoIcon sx={{ color: 'primary.main', fontSize: 16 }} />
          <Typography variant="caption" fontWeight={600} sx={{ fontSize: '0.75rem' }}>
            Image Settings
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ p: 0.25 }}>
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onChange={(e, v) => setActiveTab(v)}
        variant="fullWidth"
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          minHeight: 32,
          '& .MuiTab-root': {
            minHeight: 32,
            textTransform: 'none',
            fontSize: '0.7rem',
            padding: '6px 8px',
            minWidth: 0
          }
        }}
      >
        <Tab label="Size" />
        <Tab label="Style" />
        <Tab label="More" />
      </Tabs>

      {/* Content */}
      <Box sx={{ p: 1.5 }}>
        {/* Size Tab */}
        {activeTab === 0 && (
          <Stack spacing={1.5}>
            {/* Dimensions */}
            <Box>
              <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
                <TextField
                  size="small"
                  type="number"
                  value={dimensions.width || ''}
                  onChange={(e) => handleDimensionChange('width', e.target.value)}
                  placeholder="W"
                  InputProps={{
                    endAdornment: <InputAdornment position="end" sx={{ fontSize: '0.7rem' }}>px</InputAdornment>,
                    inputProps: { min: 0 },
                    sx: { fontSize: '0.75rem', height: 32 }
                  }}
                  sx={{ flex: 1 }}
                />
                <IconButton
                  size="small"
                  onClick={() => setMaintainRatio(!maintainRatio)}
                  sx={{
                    color: maintainRatio ? 'primary.main' : 'text.secondary',
                    bgcolor: maintainRatio ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
                    width: 24,
                    height: 24
                  }}
                >
                  {maintainRatio ? <LinkIcon sx={{ fontSize: 14 }} /> : <LinkOffIcon sx={{ fontSize: 14 }} />}
                </IconButton>
                <TextField
                  size="small"
                  type="number"
                  value={dimensions.height || ''}
                  onChange={(e) => handleDimensionChange('height', e.target.value)}
                  placeholder="H"
                  InputProps={{
                    endAdornment: <InputAdornment position="end" sx={{ fontSize: '0.7rem' }}>px</InputAdornment>,
                    inputProps: { min: 0 },
                    sx: { fontSize: '0.75rem', height: 32 }
                  }}
                  sx={{ flex: 1 }}
                />
              </Box>
            </Box>

            {/* Quick Size Presets */}
            <Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 0.5 }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => applyPresetSize('small')}
                  sx={{ 
                    textTransform: 'none', 
                    fontSize: '0.65rem',
                    minWidth: 0,
                    px: 1,
                    py: 0.5,
                    height: 28
                  }}
                >
                  S
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => applyPresetSize('medium')}
                  sx={{ 
                    textTransform: 'none', 
                    fontSize: '0.65rem',
                    minWidth: 0,
                    px: 1,
                    py: 0.5,
                    height: 28
                  }}
                >
                  M
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => applyPresetSize('large')}
                  sx={{ 
                    textTransform: 'none', 
                    fontSize: '0.65rem',
                    minWidth: 0,
                    px: 1,
                    py: 0.5,
                    height: 28
                  }}
                >
                  L
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => applyPresetSize('full')}
                  sx={{ 
                    textTransform: 'none', 
                    fontSize: '0.65rem',
                    minWidth: 0,
                    px: 1,
                    py: 0.5,
                    height: 28
                  }}
                >
                  Full
                </Button>
              </Box>
            </Box>

            <Divider />

            {/* Combined Alignment and Float */}
            <Box>
              <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary', mb: 0.5, display: 'block' }}>
                Position
              </Typography>
              <ToggleButtonGroup
                value={settings.float ? `float-${settings.float}` : `align-${settings.align}`}
                exclusive
                onChange={(e, value) => {
                  if (value) {
                    if (value.startsWith('align-')) {
                      const align = value.replace('align-', '');
                      setSettings({ ...settings, align, float: null });
                      onUpdate({ 'data-align': align, 'data-float': false });
                    } else if (value.startsWith('float-')) {
                      const float = value.replace('float-', '');
                      setSettings({ ...settings, float, align: 'left' });
                      onUpdate({ 'data-float': float, 'data-align': false });
                    }
                  }
                }}
                fullWidth
                size="small"
              >
                <ToggleButton value="float-left" sx={{ py: 0.5, px: 0.5 }}>
                  <Tooltip title="Float Left (Text wraps right)">
                    <FloatLeftIcon sx={{ fontSize: 16 }} />
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value="align-left" sx={{ py: 0.5, px: 0.5 }}>
                  <Tooltip title="Align Left (No text wrapping)">
                    <AlignLeftIcon sx={{ fontSize: 16 }} />
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value="align-center" sx={{ py: 0.5, px: 0.5 }}>
                  <Tooltip title="Align Center">
                    <AlignCenterIcon sx={{ fontSize: 16 }} />
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value="align-right" sx={{ py: 0.5, px: 0.5 }}>
                  <Tooltip title="Align Right (No text wrapping)">
                    <AlignRightIcon sx={{ fontSize: 16 }} />
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value="float-right" sx={{ py: 0.5, px: 0.5 }}>
                  <Tooltip title="Float Right (Text wraps left)">
                    <FloatRightIcon sx={{ fontSize: 16, transform: 'scaleX(-1)' }} />
                  </Tooltip>
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Stack>
        )}

        {/* Style Tab */}
        {activeTab === 1 && (
          <Stack spacing={1.5}>
            {/* Margins */}
            <Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.75 }}>
                <TextField
                  size="small"
                  label="Top"
                  type="number"
                  value={settings.margins.top}
                  onChange={(e) => {
                    const margins = { ...settings.margins, top: parseInt(e.target.value) || 0 };
                    setSettings({ ...settings, margins });
                    onUpdate({ 'data-margin-top': margins.top });
                  }}
                  InputProps={{ 
                    endAdornment: <InputAdornment position="end" sx={{ fontSize: '0.7rem' }}>px</InputAdornment>,
                    sx: { fontSize: '0.75rem', height: 32 }
                  }}
                />
                <TextField
                  size="small"
                  label="Right"
                  type="number"
                  value={settings.margins.right}
                  onChange={(e) => {
                    const margins = { ...settings.margins, right: parseInt(e.target.value) || 0 };
                    setSettings({ ...settings, margins });
                    onUpdate({ 'data-margin-right': margins.right });
                  }}
                  InputProps={{ 
                    endAdornment: <InputAdornment position="end" sx={{ fontSize: '0.7rem' }}>px</InputAdornment>,
                    sx: { fontSize: '0.75rem', height: 32 }
                  }}
                />
                <TextField
                  size="small"
                  label="Bottom"
                  type="number"
                  value={settings.margins.bottom}
                  onChange={(e) => {
                    const margins = { ...settings.margins, bottom: parseInt(e.target.value) || 0 };
                    setSettings({ ...settings, margins });
                    onUpdate({ 'data-margin-bottom': margins.bottom });
                  }}
                  InputProps={{ 
                    endAdornment: <InputAdornment position="end" sx={{ fontSize: '0.7rem' }}>px</InputAdornment>,
                    sx: { fontSize: '0.75rem', height: 32 }
                  }}
                />
                <TextField
                  size="small"
                  label="Left"
                  type="number"
                  value={settings.margins.left}
                  onChange={(e) => {
                    const margins = { ...settings.margins, left: parseInt(e.target.value) || 0 };
                    setSettings({ ...settings, margins });
                    onUpdate({ 'data-margin-left': margins.left });
                  }}
                  InputProps={{ 
                    endAdornment: <InputAdornment position="end" sx={{ fontSize: '0.7rem' }}>px</InputAdornment>,
                    sx: { fontSize: '0.75rem', height: 32 }
                  }}
                />
              </Box>
            </Box>

            <Divider />

            {/* Border */}
            <Box sx={{ display: 'flex', gap: 0.75 }}>
              <TextField
                size="small"
                label="Border"
                type="number"
                value={settings.border.width}
                onChange={(e) => {
                  const border = { ...settings.border, width: parseInt(e.target.value) || 0 };
                  setSettings({ ...settings, border });
                  onUpdate({ 'data-border-width': border.width });
                }}
                InputProps={{ 
                  endAdornment: <InputAdornment position="end" sx={{ fontSize: '0.7rem' }}>px</InputAdornment>,
                  sx: { fontSize: '0.75rem', height: 32 }
                }}
                sx={{ flex: 1 }}
              />
              <TextField
                size="small"
                type="color"
                value={settings.border.color}
                onChange={(e) => {
                  const border = { ...settings.border, color: e.target.value };
                  setSettings({ ...settings, border });
                  onUpdate({ 'data-border-color': border.color });
                }}
                InputProps={{ sx: { height: 32 } }}
                sx={{ width: 60 }}
              />
              <TextField
                size="small"
                label="Radius"
                type="number"
                value={settings.border.radius}
                onChange={(e) => {
                  const border = { ...settings.border, radius: parseInt(e.target.value) || 0 };
                  setSettings({ ...settings, border });
                  onUpdate({ 'data-border-radius': border.radius });
                }}
                InputProps={{ 
                  endAdornment: <InputAdornment position="end" sx={{ fontSize: '0.7rem' }}>px</InputAdornment>,
                  sx: { fontSize: '0.75rem', height: 32 }
                }}
                sx={{ flex: 1 }}
              />
            </Box>

            {/* Shadow */}
            <ToggleButtonGroup
              value={settings.shadow}
              exclusive
              onChange={(e, value) => {
                if (value) {
                  setSettings({ ...settings, shadow: value });
                  onUpdate({ 'data-shadow': value === 'none' ? false : value });
                }
              }}
              fullWidth
              size="small"
            >
              <ToggleButton value="none" sx={{ py: 0.5, fontSize: '0.7rem' }}>None</ToggleButton>
              <ToggleButton value="small" sx={{ py: 0.5, fontSize: '0.7rem' }}>Small</ToggleButton>
              <ToggleButton value="medium" sx={{ py: 0.5, fontSize: '0.7rem' }}>Med</ToggleButton>
              <ToggleButton value="large" sx={{ py: 0.5, fontSize: '0.7rem' }}>Large</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        )}

        {/* More Tab */}
        {activeTab === 2 && (
          <Stack spacing={1.5}>
            {/* Alt Text */}
            <TextField
              size="small"
              label="Alt Text"
              value={settings.alt}
              onChange={(e) => {
                setSettings({ ...settings, alt: e.target.value });
                onUpdate({ alt: e.target.value });
              }}
              fullWidth
              multiline
              rows={2}
              InputProps={{ sx: { fontSize: '0.75rem' } }}
              InputLabelProps={{ sx: { fontSize: '0.75rem' } }}
              sx={{ '& .MuiFormHelperText-root': { fontSize: '0.65rem' } }}
              helperText="Screen reader description"
            />

            {/* Link */}
            <TextField
              size="small"
              label="Link URL"
              value={settings.link}
              onChange={(e) => {
                setSettings({ ...settings, link: e.target.value });
                onUpdate({ 'data-link': e.target.value });
              }}
              fullWidth
              placeholder="https://example.com"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LinkIcon sx={{ fontSize: 16 }} />
                  </InputAdornment>
                ),
                sx: { fontSize: '0.75rem', height: 32 }
              }}
              InputLabelProps={{ sx: { fontSize: '0.75rem' } }}
            />
          </Stack>
        )}
      </Box>

      {/* Footer Actions */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 0.75,
          px: 1.5,
          py: 1,
          borderTop: 1,
          borderColor: 'divider',
          bgcolor: alpha(theme.palette.action.hover, 0.04)
        }}
      >
        <Button
          size="small"
          variant="text"
          onClick={() => {
            // Reset to original
            onUpdate({
              width: image.naturalWidth,
              height: image.naturalHeight,
              'data-align': 'left',
              'data-float': false,
              'data-margin-top': false,
              'data-margin-right': false,
              'data-margin-bottom': false,
              'data-margin-left': false,
              'data-border-width': false,
              'data-shadow': false
            });
            onClose();
          }}
          sx={{ 
            fontSize: '0.75rem',
            minWidth: 0,
            px: 1.5,
            py: 0.5,
            height: 28
          }}
        >
          Reset
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={onClose}
          sx={{ 
            fontSize: '0.75rem',
            minWidth: 0,
            px: 2,
            py: 0.5,
            height: 28
          }}
        >
          Done
        </Button>
      </Box>
    </Box>
  );
};

// Create theme based on mode
const createCustomTheme = (mode = 'light') => {
  const isDark = mode === 'dark';
  
  return createTheme({
    palette: {
      mode: isDark ? 'dark' : 'light',
      primary: {
        main: '#0F3A61',
        contrastText: '#ffffff'
      },
      secondary: {
        main: '#f2c526',
        contrastText: '#000000'
      },
      background: {
        default: isDark ? '#121212' : '#ffffff',
        paper: isDark ? '#1e1e1e' : '#ffffff'
      },
      text: {
        primary: isDark ? 'rgba(255, 255, 255, 0.87)' : 'rgba(0, 0, 0, 0.87)',
        secondary: isDark ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)'
      },
      divider: isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
      action: {
        hover: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
        selected: isDark ? 'rgba(255, 255, 255, 0.16)' : 'rgba(0, 0, 0, 0.08)'
      }
    },
    components: {
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : '#ffffff',
              '& fieldset': {
                borderColor: isDark ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)',
              },
              '&:hover fieldset': {
                borderColor: isDark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.87)',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#0F3A61',
              },
            },
          },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            borderColor: isDark ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)',
            color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
            '&.Mui-selected': {
              backgroundColor: '#0F3A61',
              color: '#ffffff',
              '&:hover': {
                backgroundColor: '#0d2f4f',
              },
            },
            '&:hover': {
              backgroundColor: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            '&:hover': {
              backgroundColor: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
            },
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: {
            backgroundColor: '#0F3A61',
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
            '&.Mui-selected': {
              color: '#0F3A61',
            },
          },
        },
      },
    },
  });
};

// Wrapper component with theme
const ImageEditToolbar = ({ themeMode, position, ...props }) => {
  const customTheme = createCustomTheme(themeMode);
  
  return (
    <ThemeProvider theme={customTheme}>
      <ImageEditToolbarContent position={position} {...props} />
    </ThemeProvider>
  );
};

export default ImageEditToolbar;